import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGithub } from '@fortawesome/free-brands-svg-icons';
import { FaPython, FaRaspberryPi } from 'react-icons/fa';
import {
  SiCplusplus,
  SiTensorflow,
  SiOpencv,
  SiNvidia,
  SiCmake,
  SiOpengl,
  SiPytorch,
  SiFastapi,
} from 'react-icons/si';
import { TbPlugConnected } from 'react-icons/tb';
import useScrollReveal from '../../hooks/useScrollReveal';

const projectData = [
  {
    name: 'CUDA Options Pricing Engine',
    tagline: 'Massively parallel Black-Scholes on GPU.',
    technologies: ['C++', 'CUDA', 'OpenGL', 'WebSockets', 'CMake'],
    techIcons: [SiCplusplus, SiNvidia, SiOpengl, TbPlugConnected, SiCmake],
    description:
      'C++ engine computing 100,000 Black-Scholes options in 0.93ms across 391 CUDA blocks, with live market data and custom OpenGL visualization.',
    detailedDescription:
      'Engineered a quantitative engine in C++ calculating 100,000 Black-Scholes options in 0.93ms via 391 CUDA blocks. Designed memory architecture to minimize PCIe bus latency and integrated a WebSocket client for live market data. Visualized computed premiums with custom OpenGL shaders.',
    achievements: [
      'Priced 100,000 Black-Scholes options in 0.93ms using 391 CUDA blocks',
      'Engineered memory architecture minimizing PCIe bus latency',
      'Integrated WebSocket client for live market data ingestion',
      'Built custom OpenGL shader pipeline for premium visualization',
    ],
    link: 'https://github.com/SoorajTharumia/CUDAOptionsHeatmap',
  },
  {
    name: 'Digital Eyes',
    tagline: 'Smart cane for the visually impaired.',
    technologies: ['Python', 'TensorFlow', 'OpenCV', 'Raspberry Pi'],
    techIcons: [FaPython, SiTensorflow, SiOpencv, FaRaspberryPi],
    description:
      'Smart white cane combining computer vision, AI/ML, and text-to-speech with CAD-designed 3D hardware running on a Raspberry Pi.',
    detailedDescription:
      'Developed a smart white cane for visually impaired users using computer vision, AI/ML, and text-to-speech technologies. Engineered the device with CAD-designed 3D parts, a Raspberry Pi, an ultrasonic sensor, a webcam, and a servo motor to detect objects, identify their distance, and provide auditory feedback. Leveraged OpenCV for live video streaming, TensorFlow for object recognition, and a custom algorithm to rotate the ultrasonic sensor for distance measurement.',
    achievements: [
      'Integrated computer vision, AI/ML, and text-to-speech',
      'CAD-designed 3D parts with Raspberry Pi and sensors',
      'OpenCV live video streaming + TensorFlow object recognition',
      'Custom algorithm for ultrasonic sensor rotation',
    ],
    link: 'https://drive.google.com/drive/folders/1i37WGHBFEHplghOnFiOVdSsRG2RzDIMb',
  },
  {
    name: 'Quoral',
    tagline: 'AI-powered coral reef monitoring device.',
    technologies: ['Python', 'Raspberry Pi', 'PyTorch', 'FastAPI', 'WebSockets'],
    techIcons: [FaPython, FaRaspberryPi, SiPytorch, SiFastapi, TbPlugConnected],
    description:
      'Waterproof Raspberry Pi streaming live video and temperature over WebSockets to a FastAPI server, classifying reef health with a YOLOv5 + K-means pipeline.',
    detailedDescription:
      'Built a waterproof Raspberry Pi device with a camera and temperature sensor that continuously observes coral reefs. Trained a YOLOv5 object detection model to 97% accuracy and layered a K-means algorithm on detected coral colors to classify reefs as bleached or healthy. Streamed live video over WebSockets to a FastAPI server on EC2 and visualized real-time health data on a React dashboard with interactive maps.',
    achievements: [
      'Trained YOLOv5 coral detection model to 97% accuracy',
      'Classified reef health via K-means on detected coral colors',
      'Streamed live video over WebSockets to FastAPI on EC2',
      'Waterproof Raspberry Pi enclosure with camera and temperature sensor',
    ],
    link: 'https://devpost.com/software/quoral',
  },
];

const ProjectCard = ({ project, index, onOpen }) => {
  const ref = useScrollReveal({ threshold: 0.1 });
  const direction =
    index === 0 ? 'reveal-left' : index === 2 ? 'reveal-right' : 'reveal';

  return (
    <div ref={ref} className={direction}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => onOpen(project)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onOpen(project);
        }}
        className="group relative h-full border border-line bg-surface cursor-pointer hover-lift transition-colors"
      >
        <div className="flex items-center justify-between px-6 py-3 border-b border-line">
          <span className="font-mono text-xs text-accent">
            {String(index + 1).padStart(2, '0')}
          </span>
          <span className="font-mono text-xs text-ink-dim">
            {project.technologies[0]}
          </span>
        </div>

        <div className="relative h-36 border-b border-line bg-bg overflow-hidden">
          <div className="absolute inset-0 grid-backdrop opacity-50" />
          <div className="relative h-full w-full flex items-center justify-center">
            <div className="flex items-center justify-center gap-6 px-6">
              {project.techIcons.slice(0, 5).map((Icon, i) => (
                <Icon
                  key={i}
                  className="text-3xl text-ink-muted group-hover:text-accent transition-colors duration-500"
                  style={{ transitionDelay: `${i * 40}ms` }}
                />
              ))}
            </div>
          </div>
          <div className="absolute top-2 left-2 w-3 h-3 border-t border-l border-accent/40 group-hover:border-accent transition-colors" />
          <div className="absolute top-2 right-2 w-3 h-3 border-t border-r border-accent/40 group-hover:border-accent transition-colors" />
          <div className="absolute bottom-2 left-2 w-3 h-3 border-b border-l border-accent/40 group-hover:border-accent transition-colors" />
          <div className="absolute bottom-2 right-2 w-3 h-3 border-b border-r border-accent/40 group-hover:border-accent transition-colors" />
        </div>

        <div className="p-6">
          <h3 className="font-display text-2xl text-ink group-hover:text-accent transition-colors">
            {project.name}
          </h3>
          <p className="mt-2 text-ink-muted text-sm">{project.tagline}</p>
          <p className="mt-5 text-ink-muted text-sm leading-relaxed">
            {project.description}
          </p>

          <div className="mt-6 flex flex-wrap gap-2">
            {project.technologies.map((t) => (
              <span
                key={t}
                className="font-mono text-[0.7rem] text-ink-muted border border-line px-2 py-0.5"
              >
                {t}
              </span>
            ))}
          </div>

          <div className="mt-8 flex items-center justify-end font-mono text-xs text-ink-dim group-hover:text-accent transition-colors">
            <span>View</span>
            <span className="ml-2 transition-transform group-hover:translate-x-1">
              →
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const Projects = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const headRef = useScrollReveal();
  const footRef = useScrollReveal();

  const openModal = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  return (
    <section id="projects" className="relative bg-bg text-ink py-28">
      <div className="relative container mx-auto px-6">
        <div ref={headRef} className="reveal max-w-4xl">
          <span className="eyebrow">04 / Projects</span>
          <h2 className="section-title mt-6 text-4xl sm:text-6xl md:text-7xl">
            Selected work<span className="text-accent">.</span>
          </h2>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projectData.map((project, index) => (
            <ProjectCard
              key={project.name}
              project={project}
              index={index}
              onOpen={openModal}
            />
          ))}
        </div>

        <div ref={footRef} className="reveal mt-14 flex justify-center">
          <a
            href="https://github.com/soorajtharumia"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost"
          >
            <FontAwesomeIcon icon={faGithub} />
            More on GitHub
            <span>→</span>
          </a>
        </div>
      </div>

      {isModalOpen && selectedProject && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in"
          onClick={closeModal}
        >
          <div
            className="relative bg-surface border border-line max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <span className="mono-caption">Project</span>
                <button
                  onClick={closeModal}
                  className="text-ink-muted hover:text-accent text-2xl leading-none"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              <h3 className="font-display text-3xl text-ink">
                {selectedProject.name}
              </h3>
              <p className="mt-1 text-accent">{selectedProject.tagline}</p>

              <div className="mt-6">
                <div className="mono-caption text-accent mb-2">Overview</div>
                <p className="text-ink-muted leading-relaxed">
                  {selectedProject.detailedDescription}
                </p>
              </div>

              {selectedProject.achievements?.length > 0 && (
                <div className="mt-6">
                  <div className="mono-caption text-accent mb-2">
                    Highlights
                  </div>
                  <ul className="space-y-2">
                    {selectedProject.achievements.map((a, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="text-accent font-mono text-xs pt-1">
                          ▸
                        </span>
                        <span className="text-ink-muted leading-relaxed">
                          {a}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-6">
                <div className="mono-caption text-accent mb-2">Technologies</div>
                <div className="flex flex-wrap gap-2">
                  {selectedProject.technologies.map((t) => (
                    <span
                      key={t}
                      className="font-mono text-xs text-ink border border-line px-2.5 py-1"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-8 flex items-center justify-end gap-3">
                <a
                  href={selectedProject.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                >
                  Open project →
                </a>
                <button onClick={closeModal} className="btn-ghost">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Projects;
